import { useLocationModules } from '@/src/hooks/location-module/location-module.hooks'
import { LocationModule } from '@/src/hooks/location-module/location-module.types'
import { checkIsModuleEnabled } from '@/src/hooks/location-module/location-module.utils'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { Text } from '@chakra-ui/react'
import { FlexCol } from '@components'
import { useEmberStore } from '@features/Ember/infrastructure/useEmberStore'
import type { TFunction } from 'i18next'
import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Navbar } from '../Navbar'
import { NavbarDivider } from '../NavbarDivider'
import { NavbarItem, type NavbarItemProps } from '../NavbarItem'

const navbarItems = (t: TFunction, isAdmin: boolean, isProcurementFreemium?: boolean): Array<Omit<NavbarItemProps, 'darkMode'>> => [
  {
    id: 'personal-info',
    label: t('profile.personalInfo'),
    selected: true,
    href: '/profile/personal-info',
  },
  {
    id: 'company-info',
    label: t('profile.companyInfo'),
    selected: false,
    href: '/profile/company-info',
  },
  {
    id: 'location-settings',
    label: t('profile.locationSettings'),
    selected: false,
    href: '/profile/location-settings',
  },
  ...(isAdmin
    ? [
        {
          id: 'users',
          label: t('profile.userManagement'),
          selected: false,
          href: '/profile/users',
        },
      ]
    : []),
  ...(isProcurementFreemium
    ? []
    : [
        {
          id: 'budget',
          label: t('profile.budgetSettings'),
          selected: false,
          href: '/profile/stromkosten-budget',
        },
      ]),
  {
    id: 'iso-50001',
    label: 'ISO 50001',
    selected: false,
    href: '/profile/iso-50001',
  },
]

interface NavbarAuthenticatedProps {
  isMobile?: boolean
  handleCloseMobile?: () => void
  darkMode?: boolean
}

export const NavbarAuthenticatedProfile: FC<NavbarAuthenticatedProps> = ({ isMobile = false, handleCloseMobile, darkMode = false }) => {
  const { t } = useTranslation()
  const session = useSessionStore()
  const isAdmin = session.user?.roles?.includes('admin')
  const { data: enabledModulesData } = useLocationModules()
  const enabledModules = enabledModulesData?.enabledModules ?? []
  const isProcurementFreemium = checkIsModuleEnabled(LocationModule.PROCUREMENT_FREEMIUM, enabledModules)
  const translatedNavbarItems = navbarItems(t, isAdmin ?? false, isProcurementFreemium)
  const location = useLocation()
  const emberStore = useEmberStore()

  useEffect(() => {
    if (emberStore.isSidebarShowing) emberStore.toggleSidebar()
  }, [])

  return (
    <Navbar isSubNavbar>
      <FlexCol height='100vh'>
        <Text color='gray.800' fontSize={24} fontWeight={500} noOfLines={1} ml={'24px'} mt={'35px'} mb={'15px'}>
          {t('profile.settings')}
        </Text>
        <NavbarDivider />

        <FlexCol mt={'-8px'} alignItems='start' w='full' px='12px' gap='4px'>
          {translatedNavbarItems?.map((navbarItem, index) => (
            <NavbarItem
              id={navbarItem.label}
              key={navbarItem.label}
              label={navbarItem.label}
              comingSoonLabel={navbarItem.comingSoonLabel}
              selected={location.pathname.includes(navbarItem.href)}
              href={navbarItem.href}
              disabled={navbarItem.disabled}
              handleCloseMobile={handleCloseMobile}
            />
          ))}
        </FlexCol>
      </FlexCol>
    </Navbar>
  )
}
